// src/modules/consent.js — Patient consent form for audio recording
import { ProfileStore } from '../stores/profile-store.js';
import { escapeHtml, showToast } from '../utils/dom.js';
import { LOCAL_PHI_RETENTION_DAYS } from '../utils/constants.js';
import { logger, apiFetchWithRefresh } from './debug.js';

// ============================================================================
// CONSENT TEXT (standard HIPAA-aligned)
// ============================================================================
const CONSENT_TEXT = `PATIENT CONSENT FOR AUDIO RECORDING

I hereby consent to the audio recording of my dental appointment on the date specified above. I understand and agree to the following:

1. This recording will be used solely for the purpose of generating clinical documentation (SOAP notes) for my dental visit.

2. The recording will be processed using artificial intelligence (AI) technology to create accurate clinical notes.

3. My recording and resulting documentation will be stored securely with encryption and accessible only to authorized dental staff.

4. I may request to revoke this consent at any time by notifying the dental office.

5. No personally identifiable health information beyond what is necessary for clinical documentation will be shared with third parties.

This consent is valid for all recordings made for the undersigned patient on the specified date of visit.`;

// ============================================================================
// HELPERS
// ============================================================================

function getTodayISO() {
    return new Date().toISOString().slice(0, 10);
}

function normalizeNameKey(firstName, lastName) {
    return (firstName + '_' + lastName).toLowerCase().replace(/\s+/g, '_');
}

function consentStorageKey(profileId, firstName, lastName, date) {
    const nameKey = normalizeNameKey(firstName, lastName);
    return `dentscribe_consent_${profileId}_${nameKey}_${date}`;
}

/**
 * Look up consent from localStorage by patient full name + date.
 * Used in note modal to find matching consent.
 */
export function findConsentForNote(patientLabel, noteDate) {
    if (!patientLabel || !noteDate) return null;
    const profileId = ProfileStore.get('currentProfileId');
    if (!profileId) return null;
    const datePart = noteDate.slice(0, 10); // YYYY-MM-DD from ISO
    // Split patientLabel into first/last — simple heuristic
    const parts = patientLabel.trim().split(/\s+/);
    if (parts.length < 2) return null;
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    const key = consentStorageKey(profileId, firstName, lastName, datePart);
    try {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : null;
    } catch (e) {
        return null;
    }
}

/**
 * Check if consent exists for today for a given patient name.
 */
function getExistingConsent(firstName, lastName) {
    const profileId = ProfileStore.get('currentProfileId');
    if (!profileId || !firstName || !lastName) return null;
    const key = consentStorageKey(profileId, firstName, lastName, getTodayISO());
    try {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : null;
    } catch (e) {
        return null;
    }
}

/**
 * Reap consent records older than LOCAL_PHI_RETENTION_DAYS (7).
 */
export function reapExpiredConsents() {
    const cutoff = Date.now() - (LOCAL_PHI_RETENTION_DAYS * 24 * 60 * 60 * 1000);
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('dentscribe_consent_')) continue;
        try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data && data.signedAt && new Date(data.signedAt).getTime() < cutoff) {
                keysToRemove.push(key);
            }
        } catch (e) {
            // corrupt entry — remove it
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    if (keysToRemove.length > 0) {
        logger.info('CONSENT', 'Reaped expired consent records', { count: keysToRemove.length });
    }
}

// ============================================================================
// SIGNATURE CANVAS
// ============================================================================

let _signatureCtx = null;
let _isDrawing = false;
let _hasSignature = false;

function initSignatureCanvas() {
    const canvas = document.getElementById('consentSignatureCanvas');
    if (!canvas) return;
    _signatureCtx = canvas.getContext('2d');
    _signatureCtx.strokeStyle = '#000';
    _signatureCtx.lineWidth = 2;
    _signatureCtx.lineCap = 'round';
    _signatureCtx.lineJoin = 'round';
    _hasSignature = false;
    _isDrawing = false;

    // Draw placeholder
    _drawPlaceholder();

    // Touch events
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);

    // Mouse events
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseUp);
}

function _drawPlaceholder() {
    if (!_signatureCtx) return;
    const canvas = _signatureCtx.canvas;
    _signatureCtx.clearRect(0, 0, canvas.width, canvas.height);
    _signatureCtx.save();
    _signatureCtx.fillStyle = '#999';
    _signatureCtx.font = '14px sans-serif';
    _signatureCtx.textAlign = 'center';
    _signatureCtx.fillText('Sign here', canvas.width / 2, canvas.height / 2);
    _signatureCtx.restore();
}

function _getCanvasCoords(canvas, e) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
    };
}

function onTouchStart(e) {
    e.preventDefault();
    if (!_hasSignature) {
        // Clear placeholder on first stroke
        _signatureCtx.clearRect(0, 0, _signatureCtx.canvas.width, _signatureCtx.canvas.height);
    }
    _isDrawing = true;
    _hasSignature = true;
    const touch = e.touches[0];
    const coords = _getCanvasCoords(_signatureCtx.canvas, touch);
    _signatureCtx.beginPath();
    _signatureCtx.moveTo(coords.x, coords.y);
    updateSignButton();
}

function onTouchMove(e) {
    e.preventDefault();
    if (!_isDrawing) return;
    const touch = e.touches[0];
    const coords = _getCanvasCoords(_signatureCtx.canvas, touch);
    _signatureCtx.lineTo(coords.x, coords.y);
    _signatureCtx.stroke();
}

function onTouchEnd() {
    _isDrawing = false;
}

function onMouseDown(e) {
    if (!_hasSignature) {
        _signatureCtx.clearRect(0, 0, _signatureCtx.canvas.width, _signatureCtx.canvas.height);
    }
    _isDrawing = true;
    _hasSignature = true;
    const coords = _getCanvasCoords(_signatureCtx.canvas, e);
    _signatureCtx.beginPath();
    _signatureCtx.moveTo(coords.x, coords.y);
    updateSignButton();
}

function onMouseMove(e) {
    if (!_isDrawing) return;
    const coords = _getCanvasCoords(_signatureCtx.canvas, e);
    _signatureCtx.lineTo(coords.x, coords.y);
    _signatureCtx.stroke();
}

function onMouseUp() {
    _isDrawing = false;
}

function updateSignButton() {
    const btn = document.getElementById('consentSubmitBtn');
    const checkbox = document.getElementById('consentAgreeCheckbox');
    if (btn) {
        btn.disabled = !(_hasSignature && checkbox && checkbox.checked);
    }
}

// ============================================================================
// MODAL: OPEN / CLOSE
// ============================================================================

export function openConsentForm() {
    const modal = document.getElementById('consentModal');
    if (!modal) return;

    // Pre-fill provider from current profile
    const profile = ProfileStore.getCurrentProfile();
    const providerInput = document.getElementById('consentProvider');
    if (providerInput && profile) {
        providerInput.value = profile.displayName || '';
    }

    // Pre-fill date with today
    const dateInput = document.getElementById('consentDate');
    if (dateInput) {
        dateInput.value = getTodayISO();
    }

    // Clear patient name fields
    const firstNameInput = document.getElementById('consentFirstName');
    const lastNameInput = document.getElementById('consentLastName');
    if (firstNameInput) firstNameInput.value = '';
    if (lastNameInput) lastNameInput.value = '';

    // Reset checkbox
    const checkbox = document.getElementById('consentAgreeCheckbox');
    if (checkbox) checkbox.checked = false;

    // Show modal
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Init canvas
    setTimeout(() => initSignatureCanvas(), 50);

    logger.info('CONSENT', 'Consent form opened');
}

export function closeConsentModal() {
    const modal = document.getElementById('consentModal');
    if (!modal) return;
    modal.classList.remove('active');
    document.body.style.overflow = '';
    _signatureCtx = null;
    _hasSignature = false;
    _isDrawing = false;
}

export function clearConsentSignature() {
    _hasSignature = false;
    _isDrawing = false;
    if (_signatureCtx) {
        _drawPlaceholder();
    }
    updateSignButton();
}

// ============================================================================
// MODAL: SUBMIT CONSENT
// ============================================================================

export function submitConsent() {
    const firstNameInput = document.getElementById('consentFirstName');
    const lastNameInput = document.getElementById('consentLastName');
    const dateInput = document.getElementById('consentDate');
    const providerInput = document.getElementById('consentProvider');
    const checkbox = document.getElementById('consentAgreeCheckbox');

    const firstName = (firstNameInput ? firstNameInput.value.trim() : '');
    const lastName = (lastNameInput ? lastNameInput.value.trim() : '');
    const date = (dateInput ? dateInput.value : getTodayISO());
    const provider = (providerInput ? providerInput.value.trim() : '');

    // Validation
    if (!firstName || !lastName) {
        showToast('Please enter patient first and last name', 'warning');
        return;
    }
    if (!checkbox || !checkbox.checked) {
        showToast('Please acknowledge the consent', 'warning');
        return;
    }
    if (!_hasSignature) {
        showToast('Please provide a signature', 'warning');
        return;
    }

    // Capture signature as data URL
    const canvas = document.getElementById('consentSignatureCanvas');
    const signatureDataUrl = canvas ? canvas.toDataURL('image/png') : '';

    const profileId = ProfileStore.get('currentProfileId');
    const consentData = {
        firstName: firstName,
        lastName: lastName,
        date: date,
        provider: provider,
        signatureDataUrl: signatureDataUrl,
        signedAt: new Date().toISOString()
    };

    // Save to localStorage
    const key = consentStorageKey(profileId, firstName, lastName, date);
    try {
        localStorage.setItem(key, JSON.stringify(consentData));
    } catch (e) {
        showToast('Failed to save consent — storage full', 'error');
        return;
    }

    // Upload to server (best-effort, non-blocking)
    _uploadConsentToServer(consentData);

    // Auto-fill patientLabel on the recording page
    const patientLabelInput = document.getElementById('patientLabel');
    if (patientLabelInput) {
        patientLabelInput.value = firstName + ' ' + lastName;
    }

    // Update badge
    updateConsentBadge();

    // Close modal + toast
    closeConsentModal();
    showToast('Consent recorded for ' + escapeHtml(firstName) + ' ' + escapeHtml(lastName), 'success');
    logger.info('CONSENT', 'Consent submitted', { date: date });
}

// ============================================================================
// CONSENT BADGE (shown on recording page)
// ============================================================================

export function updateConsentBadge() {
    const badge = document.getElementById('consentBadge');
    if (!badge) return;
    const patientLabelInput = document.getElementById('patientLabel');
    const name = patientLabelInput ? patientLabelInput.value.trim() : '';
    if (!name) {
        badge.classList.add('d-none');
        return;
    }
    const parts = name.split(/\s+/);
    if (parts.length < 2) {
        badge.classList.add('d-none');
        return;
    }
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    const consent = getExistingConsent(firstName, lastName);
    if (consent) {
        badge.classList.remove('d-none');
        badge.textContent = '✓ Consent on file';
    } else {
        badge.classList.add('d-none');
    }
}

// ============================================================================
// CONSENT PDF (download from note modal)
// ============================================================================

export async function downloadConsentPdf(patientLabel, noteDate, noteId) {
    // Try localStorage first (fast path)
    let consent = findConsentForNote(patientLabel, noteDate);

    // Fall back to server API (cross-device access)
    if (!consent && noteId) {
        consent = await _fetchConsentFromServer(noteId);
    }

    if (!consent) {
        showToast('No consent found for this patient/date', 'warning');
        return;
    }

    // Dynamic import jsPDF (bundled by esbuild)
    let jsPDF;
    try {
        const mod = await import('jspdf');
        jsPDF = mod.jsPDF || mod.default;
    } catch (e) {
        showToast('Failed to load PDF library', 'error');
        logger.error('CONSENT', 'jsPDF import failed', e);
        return;
    }

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 25;

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PATIENT CONSENT FOR AUDIO RECORDING', pageWidth / 2, y, { align: 'center' });
    y += 12;

    // Line separator
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;

    // Info fields
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Patient Name:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(consent.firstName + ' ' + consent.lastName, margin + 35, y);
    y += 7;

    doc.setFont('helvetica', 'bold');
    doc.text('Date of Visit:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(consent.date, margin + 35, y);
    y += 7;

    doc.setFont('helvetica', 'bold');
    doc.text('Provider:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(consent.provider || 'N/A', margin + 35, y);
    y += 7;

    doc.setFont('helvetica', 'bold');
    doc.text('Practice:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text('DentScribe Practice', margin + 35, y);
    y += 12;

    // Line separator
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;

    // Consent body text
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(CONSENT_TEXT.replace(/^PATIENT CONSENT FOR AUDIO RECORDING\n\n/, ''), contentWidth);
    for (let i = 0; i < lines.length; i++) {
        if (y > 260) {
            doc.addPage();
            y = 20;
        }
        doc.text(lines[i], margin, y);
        y += 5;
    }
    y += 10;

    // Signature
    if (y > 230) {
        doc.addPage();
        y = 20;
    }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Patient Signature:', margin, y);
    y += 5;

    // Draw signature image
    if (consent.signatureDataUrl) {
        try {
            doc.addImage(consent.signatureDataUrl, 'PNG', margin, y, 60, 30);
            y += 35;
        } catch (e) {
            doc.text('[Signature captured digitally]', margin, y + 5);
            y += 12;
        }
    }

    // Signed timestamp
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Signed electronically: ' + new Date(consent.signedAt).toLocaleString(), margin, y);

    // Download
    const safeFirst = consent.firstName.replace(/[^a-zA-Z0-9]/g, '');
    const safeLast = consent.lastName.replace(/[^a-zA-Z0-9]/g, '');
    const filename = `consent-${safeFirst}-${safeLast}-${consent.date}.pdf`;
    doc.save(filename);

    showToast('Consent PDF downloaded', 'success');
    logger.info('CONSENT', 'PDF downloaded', { date: consent.date });
}

// ============================================================================
// SERVER SYNC HELPERS (cross-device consent access)
// ============================================================================

async function _uploadConsentToServer(consentData) {
    try {
        const body = {
            firstName: consentData.firstName,
            lastName: consentData.lastName,
            date: consentData.date,
            provider: consentData.provider,
            signatureDataUrl: consentData.signatureDataUrl,
            signedAt: consentData.signedAt,
        };
        const profileId = ProfileStore.get('currentProfileId');
        if (profileId) body.profileId = profileId;

        const res = await apiFetchWithRefresh('/consent', {
            method: 'POST',
            body: JSON.stringify(body),
        });
        if (res.ok) {
            const data = await res.json();
            logger.info('CONSENT', 'Consent uploaded to server', { consentId: data.consentId });
        } else {
            logger.warn('CONSENT', 'Server consent upload failed (non-fatal)', { status: res.status });
        }
    } catch (e) {
        // Non-blocking — consent is still in localStorage
        logger.warn('CONSENT', 'Server consent upload error (non-fatal)', { error: e.message });
    }
}

async function _fetchConsentFromServer(noteId) {
    try {
        const res = await apiFetchWithRefresh('/notes/' + encodeURIComponent(noteId) + '/consent', {
            method: 'GET',
        });
        if (!res.ok) return null;
        const data = await res.json();
        return data.consent || null;
    } catch (e) {
        logger.warn('CONSENT', 'Server consent fetch failed', { error: e.message });
        return null;
    }
}

// ============================================================================
// CHECKBOX HANDLER (wired via data-on-change in HTML)
// ============================================================================

export function onConsentCheckboxChange() {
    updateSignButton();
}

// ============================================================================
// FEATURE FLAG VISIBILITY
// ============================================================================

export function updateConsentVisibility(enabled) {
    const consentBtn = document.getElementById('consentFormBtn');
    if (consentBtn) {
        consentBtn.style.display = enabled ? '' : 'none';
    }
}
